/**
 * Authors: mariushirjanu
 * Created: 23/12/2020
 */
import { Injectable } from '@angular/core';
import { FileUploadDialogComponent } from './file-upload-dialog.component';
import { AuthService } from '../services/auth/auth.service';
import { IDocument, IObject } from '@type_definitions/datatypes';
import { util } from '../util';
import { DialogSize, GaMessageService, IDialog, IDialogButton } from '../message/ga-message.service';
import { IProductImagesHolder } from '@app/main/globalproduct/global-product-data.service';
import { GaDeferredService, IGADeferred } from '../services/ga-deferred.service';
import { TranslationService } from '../translation/translation.service';
import { DocumentDataService, DOCUMENTS_URL, DocumentTableReference } from '@app/shared/document/document-data.service';

export interface IFileUpload {
  status:string;
  response:IFileUploadResponse;
}

export interface IFileUploadData {
  document:IDocument;
  webshopDocumentContext?:IObject;
}

export interface IFileUploadResponse {
  status:string;
  data:IDocument|IProductImagesHolder|number;
  message?:string;
}

export interface IFileUploadFailResponse {
  timestamp:number;
  status:ResponseStatus;
  error:string;
  message:string;
  path:string;
}

interface IFileUploadDialogOptions {
  title?:string;
  label?:string;
  documentType?:string;
  hideDocumentType?:boolean;
  editableDocumentType?:boolean;
  autoselect?:boolean;
  document:IDocument;
  recordId?:number;
  tableReference?:DocumentTableReference;
}

export enum ResponseStatus {
  FAIL = 'FAIL',
  OK = 'OK'
}

@Injectable()
export class FileUploadService {
  private fileUploadDialog:IDialog;

  constructor(
    private authService:AuthService,
    private messageService:GaMessageService,
    private documentDataService:DocumentDataService,
    private deferredService:GaDeferredService,
    private translationService:TranslationService
  ) {
    /***/
  }

  public showFileUploadDialog = (uploadUrl:string, options?:IFileUploadDialogOptions):Promise<IDocument> => {
    const deferred:IGADeferred<IDocument> = this.deferredService.defer();
    this.fileUploadDialog = this.messageService.showDialog({
      component: FileUploadDialogComponent,
      wrapperComponent: false,
      inputs: {
        title: options?.title,
        label: options?.label,
        document: options?.document,
        documentType: options?.documentType,
        hideDocumentType: options?.hideDocumentType,
        editableDocumentType: options?.editableDocumentType,
        uploadUrl: this.authService.authenticatedUrl(uploadUrl),
        autoselect: util.notNullOrUndefinedOrEmpty(options?.autoselect)? options.autoselect : true,
        tableReference: options?.tableReference,
        recordId: options?.recordId
      },
      outputs: {
        onSave: (result:IDocument) => {
          this.closeDialog();
          deferred.resolve(result);
        },
        onClose: () => {
          this.closeDialog();
          deferred.reject();
        }
      },
      size: DialogSize.MD
    });

    return deferred.promise;
  };

  private closeDialog():void {
    this.fileUploadDialog.hide();
  }

  /* this method can be used in 2 ways:
  * 1) by providing a document object: showDeleteFileConfirmationDialog(document) OR
  * 2) by providing a delete URL + confirmation message: showDeleteFileConfirmationDialog(undefined, 'rest/compman/company/21/logo', 'Are you sure you want to delete this logo?')
  */
  public showDeleteFileConfirmationDialog(document?:IDocument, url?:string, message?:string):Promise<void> {
    const fileUrl:string = url || `${DOCUMENTS_URL}/${document?.id}`;
    const deferred:IGADeferred<void> = this.deferredService.defer();

    const onDelete = ():void => {
      buttons[0].busy = true;
      this.documentDataService.deleteDocumentByUrl(fileUrl)
        .then(() => {
          deferred.resolve();
          dialog.hide();
        })
        .catch(deferred.reject)
        .finally(() => {
          buttons[0].busy = false;
        });
    };

    const buttons:IDialogButton[] = [ {
      label: this.translationService.instant('GA.BUTTONS.DELETE'),
      className: 'btn-danger',
      onClick: onDelete
    }, {
      label: this.translationService.instant('GA.BUTTONS.DONT_LABEL', { label: 'GA.BUTTONS.DELETE' }),
      className: 'btn-default',
      onClick: () => {
        dialog.hide();
        deferred.reject();
      }
    } ];

    const dialog:IDialog = this.messageService.showDialog({
      title: this.translationService.instant('GA.BUTTONS.DELETE'),
      message: (message || `<p>${this.translationService.instant('APP.COMPONENTS.FILEUPLOAD.DELETE_CONFIRMATION')} <b>${document?.fileName}</b></p>`) + `<p class="mt-3"><img src="${fileUrl}" alt="" /></p>`,
      buttons,
      size: DialogSize.LG
    });

    return deferred.promise;
  }
}